/**
 * V3 Day 2 — Cinematic ProjectShowcase slide list (24 total).
 *
 * Layout (1-indexed slot in the user's brief):
 *   1-7   building exteriors  (5 day + 2 night, hero = exterior-night-1)
 *   8     chapter card        — "Lifestyle / أسلوب حياة"
 *   9-17  lifestyle hero shots
 *   18    chapter card        — "Details / التفاصيل"
 *   19-24 detail / accent shots
 *
 * URLs come from src/data/assets-manifest.json so this file does not
 * hardcode any host. Captions are inline (24 entries doesn't justify
 * a separate i18n file). Keep titles 1-3 words; subtitles 5-10.
 */

import manifest from '@data/assets-manifest.json';

type ManifestRecord = Record<string, string>;
const m = manifest as unknown as {
  building: ManifestRecord;
  amenities: ManifestRecord;
};

export type ShowcaseImageSlide = {
  kind: 'image';
  id: string;
  url: string;
  titleAr: string;
  titleEn: string;
  subtitleAr?: string;
  subtitleEn?: string;
};

export type ShowcaseChapterSlide = {
  kind: 'chapter';
  id: string;
  titleAr: string;
  titleEn: string;
  subtitleAr?: string;
  subtitleEn?: string;
};

export type ShowcaseSlide = ShowcaseImageSlide | ShowcaseChapterSlide;

const B = m.building;
const A = m.amenities;

export const SHOWCASE_SLIDES: ShowcaseSlide[] = [
  // ── 1-7 Building exteriors ──────────────────────────────────────
  {
    kind: 'image',
    id: 'exterior-night-1',
    url: B['exterior-night-1'],
    titleAr: 'البرج الأيقوني',
    titleEn: 'The Iconic Tower',
    subtitleAr: 'تصميم ينبض بالأناقة في قلب الخوض',
    subtitleEn: 'Refined architecture in the heart of Al Khoudh',
  },
  {
    kind: 'image',
    id: 'exterior-night-2',
    url: B['exterior-night-2'],
    titleAr: 'إطلالات ساحرة',
    titleEn: 'Captivating Views',
    subtitleAr: 'حيث تلتقي المعاصرة بالضوء',
    subtitleEn: 'Where modernity meets light',
  },
  {
    kind: 'image',
    id: 'exterior-13',
    url: B['exterior-13'],
    titleAr: 'بصمة معمارية',
    titleEn: 'Architectural Signature',
  },
  {
    kind: 'image',
    id: 'exterior-30',
    url: B['exterior-30'],
    titleAr: 'خطوط معاصرة',
    titleEn: 'Modern Lines',
  },
  {
    kind: 'image',
    id: 'exterior-night-3',
    url: B['exterior-night-3'],
    titleAr: 'الواجهة المضيئة',
    titleEn: 'Illuminated Façade',
  },
  {
    kind: 'image',
    id: 'exterior-31',
    url: B['exterior-31'],
    titleAr: 'نهاراً وليلاً',
    titleEn: 'Day and Night',
  },
  {
    kind: 'image',
    id: 'exterior-night-4',
    url: B['exterior-night-4'],
    titleAr: 'حضور لافت',
    titleEn: 'A Striking Presence',
  },

  // ── 8 Chapter — Lifestyle ───────────────────────────────────────
  {
    kind: 'chapter',
    id: 'chapter-lifestyle',
    titleAr: 'أسلوب حياة',
    titleEn: 'A Way of Life',
    subtitleAr: 'مرافق فاخرة لتجربة سكنية استثنائية',
    subtitleEn: 'Premium amenities for an extraordinary lifestyle',
  },

  // ── 9-17 Lifestyle hero ─────────────────────────────────────────
  {
    kind: 'image',
    id: 'pool',
    url: A['pool'],
    titleAr: 'حمام السباحة',
    titleEn: 'Outdoor Pool',
    subtitleAr: 'إطلالة بانورامية ومياه صافية',
    subtitleEn: 'Panoramic views and crystal waters',
  },
  {
    kind: 'image',
    id: 'spa',
    url: A['spa'],
    titleAr: 'السبا',
    titleEn: 'The Spa',
    subtitleAr: 'لحظات استرخاء فاخرة',
    subtitleEn: 'Moments of premium relaxation',
  },
  {
    kind: 'image',
    id: 'sauna-2',
    url: A['sauna-2'],
    titleAr: 'الساونا',
    titleEn: 'Sauna',
    subtitleAr: 'حرارة جافة بمعايير عالمية',
    subtitleEn: 'Dry heat to international standards',
  },
  {
    kind: 'image',
    id: 'gym-cinematic',
    url: A['gym-cinematic'],
    titleAr: 'نادي اللياقة',
    titleEn: 'Fitness Club',
    subtitleAr: 'أحدث الأجهزة في بيئة احترافية',
    subtitleEn: 'Latest equipment, professional setting',
  },
  {
    kind: 'image',
    id: 'lobby',
    url: A['lobby'],
    titleAr: 'صالة الاستقبال',
    titleEn: 'Grand Lobby',
    subtitleAr: 'أول انطباع لأهم اللحظات',
    subtitleEn: 'A first impression that lasts',
  },
  {
    kind: 'image',
    id: 'lobby-2',
    url: A['lobby-2'],
    titleAr: 'ترحيب راقٍ',
    titleEn: 'Refined Welcome',
  },
  {
    kind: 'image',
    id: 'roof-terrace',
    url: A['roof-terrace'],
    titleAr: 'تراس السطح',
    titleEn: 'Rooftop Terrace',
    subtitleAr: 'إطلالة ٣٦٠ درجة على الخوض',
    subtitleEn: 'A 360° view of Al Khoudh',
  },
  {
    kind: 'image',
    id: 'entrance',
    url: A['entrance'],
    titleAr: 'المدخل الرئيسي',
    titleEn: 'Main Entrance',
    subtitleAr: 'تصميم استقبال يليق بالساكنين',
    subtitleEn: 'A welcome worthy of its residents',
  },
  {
    kind: 'image',
    id: 'parking-cinematic',
    url: A['parking-cinematic'],
    titleAr: 'مواقف ذكية',
    titleEn: 'Premium Parking',
    subtitleAr: 'مساحات منظمة وآمنة',
    subtitleEn: 'Organised and secure spaces',
  },

  // ── 18 Chapter — Details ────────────────────────────────────────
  {
    kind: 'chapter',
    id: 'chapter-details',
    titleAr: 'في التفاصيل تكمن الفخامة',
    titleEn: 'Luxury Lives in the Details',
    subtitleAr: 'اللمسات التي ستلاحظها كل يوم',
    subtitleEn: 'The touches you will notice every day',
  },

  // ── 19-24 Details / accent shots ────────────────────────────────
  {
    kind: 'image',
    id: 'lobby-3',
    url: A['lobby-3'],
    titleAr: 'زوايا اللوبي',
    titleEn: 'Lobby Angles',
  },
  {
    kind: 'image',
    id: 'sauna-1',
    url: A['sauna-1'],
    titleAr: 'خشب طبيعي',
    titleEn: 'Natural Wood',
  },
  {
    kind: 'image',
    id: 'gym-1',
    url: A['gym-1'],
    titleAr: 'مساحات تدريب',
    titleEn: 'Training Spaces',
  },
  {
    kind: 'image',
    id: 'bbq',
    url: A['bbq'],
    titleAr: 'ركن الشواء',
    titleEn: 'BBQ Corner',
    subtitleAr: 'تجمعات عائلية لا تُنسى',
    subtitleEn: 'Unforgettable family gatherings',
  },
  {
    kind: 'image',
    id: 'kids-play',
    url: A['kids-play'],
    titleAr: 'ركن الأطفال',
    titleEn: 'Kids Play Area',
    subtitleAr: 'مساحة آمنة ومرحة',
    subtitleEn: 'A safe and playful space',
  },
  {
    kind: 'image',
    id: 'lobby-5',
    url: A['lobby-5'],
    titleAr: 'تفاصيل المدخل',
    titleEn: 'Entry Details',
  },
];
